import { getStorageItem, setStorageItem, WebStorageType } from "../../../common-deprecated/utils/storageUtils";
import type { UscFinanceQuoteSessionType } from "./financeUtils";
import type { CommonSettingsType } from "../../../common-deprecated/settings/fetchCommonSettings";
import type { UscVehicleReducerType } from "../redux/reducers/UscVehicleReducer";
import type { UscInsuranceQuoteType } from "./insuranceUtils";
import type { UscCarDeliveryConfig, UscCarDeliveryConfigSessionType } from "./carDeliveryUtils";
import type { UscContext } from "../../shared-logic/types/UscCommonTypes";

// ----------------------------------------------------------------------
// Helper types and consts
// ----------------------------------------------------------------------
const SESSION_STORAGE_ID = "com.toyota.tme.t1.usedStockCars";

export type UscSessionStorageItemType = {
    taxLocationId?: string;
    financeQuote?: { [vehicleForSaleId: string]: UscFinanceQuoteSessionType | null };
    insuranceQuote?: { [vehicleForSaleId: string]: UscInsuranceQuoteType | null };
    deliveryConfig?: { [dealerId: string]: UscCarDeliveryConfig | null } | null;
};

export type UscSessionStorageParams = {
    context: UscContext;
    country: string;
    language: string;
};

// ----------------------------------------------------------------------
// Storage fns
// ----------------------------------------------------------------------

export const getUscSessionStorage = (params: UscSessionStorageParams): UscSessionStorageItemType | null => {
    return getStorageItem({ ...params, storageId: SESSION_STORAGE_ID }, WebStorageType.session);
};

export const setUscSessionStorage = (params: UscSessionStorageParams, storageItem: UscSessionStorageItemType): void => {
    setStorageItem({ ...params, storageId: SESSION_STORAGE_ID }, storageItem, WebStorageType.session);
};

// ----------------------------------------------------------------------
// Tax Location
// ----------------------------------------------------------------------

export const getTaxLocationIdFromStorage = (params: UscSessionStorageParams): string => {
    return getUscSessionStorage(params)?.taxLocationId || "";
};

export const setTaxLocationIdToStorage = (params: UscSessionStorageParams, taxLocationId: string): void => {
    setUscSessionStorage(params, { taxLocationId });
};

// ----------------------------------------------------------------------
// Finance and insurance quotes
// ----------------------------------------------------------------------
export const getInsuranceQuoteFromStorage = (
    params: UscSessionStorageParams,
    vehicleForSaleId: string,
): UscInsuranceQuoteType | null => {
    return getUscSessionStorage(params)?.insuranceQuote?.[vehicleForSaleId] || null;
};

export const setInsuranceQuoteToStorage = (
    params: UscSessionStorageParams,
    quote: UscInsuranceQuoteType | null,
    vehicleForSaleId: string,
): void => {
    setUscSessionStorage(params, { insuranceQuote: { [vehicleForSaleId]: quote } });
};

export const getFinanceQuoteFromStorage = (
    params: UscSessionStorageParams,
    vehicleForSaleId: string,
): UscFinanceQuoteSessionType | null => {
    return getUscSessionStorage(params)?.financeQuote?.[vehicleForSaleId] || null;
};

export const setFinanceQuoteToStorage = (
    params: UscSessionStorageParams,
    quote: UscFinanceQuoteSessionType,
    vehicleForSaleId: string,
): void => {
    setUscSessionStorage(params, { financeQuote: { [vehicleForSaleId]: quote } });
};

// ----------------------------------------------------------------------
// Car Delivery
// ----------------------------------------------------------------------

export const getCarDeliveryFromStorage = (params: UscSessionStorageParams): UscCarDeliveryConfigSessionType | null => {
    return getUscSessionStorage(params)?.deliveryConfig || null;
};

export const setCarDeliveryToStorage = (
    params: UscSessionStorageParams,
    deliveryConfig: UscCarDeliveryConfig,
    /** This is the dealerId of the current vehicle, NOT the dealerId in the deliveryConfig */
    dealerId: string,
): void => {
    setUscSessionStorage(params, { deliveryConfig: { [dealerId]: deliveryConfig } });
};

export const removeCarDeliveryFromStorage = (params: UscSessionStorageParams): void => {
    setUscSessionStorage(params, { deliveryConfig: null });
};

// ----------------------------------------------------------------------
// Helper fns
// ----------------------------------------------------------------------

export const getSessionStorageParams = ({
    commonSettings,
    vehicle,
}: {
    commonSettings: CommonSettingsType;
    vehicle: UscVehicleReducerType;
}): UscSessionStorageParams => ({
    country: commonSettings.country,
    language: commonSettings.language,
    context: vehicle.type,
});
